import type {
  CaseCptLine,
  Laterality,
  ScheduledCase,
  SurgeonTemplate,
  PreLoadedDoc,
} from '@/lib/templates/types';
import { getSecondaryDefaultModifiers } from '@/lib/modifiers/xepsu';

export interface OrthopedicPacketDoc extends PreLoadedDoc {
  extracted_text: string;
  source_path: string;
}

export interface OrthopedicPacketProfile {
  scheduledCase: ScheduledCase;
  template: SurgeonTemplate;
  docs: OrthopedicPacketDoc[];
  preAuthDocs: OrthopedicPacketDoc[];
  postOpDocs: OrthopedicPacketDoc[];
  clinicalSummary: string;
  laterality: Laterality;
  clinicalMetrics: Record<string, unknown>;
}

export interface OrthopedicPacketPostOpCase {
  scheduledCase: ScheduledCase;
  template: SurgeonTemplate;
  laterality: Laterality;
  clinicalMetrics: Record<string, unknown>;
  cptLines: CaseCptLine[];
}

const DEMO_SURGEON_ID = 'demo-surgeon-john-larkin';
const DEMO_FACILITY_ID = 'demo-facility-st-elizabeth-florence';
const DEMO_NOW = '2026-05-18T12:00:00.000Z';

export const ORTHOPEDIC_PACKET_DEMO_FACILITY = {
  id: DEMO_FACILITY_ID,
  name: 'St. Elizabeth Florence',
  type: 'Hospital outpatient',
} as const;

const ackmanPreAuthFacts = [
  'Patient: Amanda Ackman. DOB 12/18/1969. Sex female. MRN 00348504. CSN 1203557652.',
  'Date of service 05/28/2026 08:30 AM at St. Elizabeth Florence.',
  'Surgeon John J. Larkin, MD. Payer Anthem BCBS.',
  'Primary procedure: Manipulation of right knee joint under general anesthesia. CPT 27570.',
  'Diagnosis: Arthrofibrosis, right knee; status post right total knee replacement performed 02/18/2026.',
  'Index right TKA source record dated 02/18/2026 confirms an uncomplicated primary right total knee arthroplasty and establishes the 90-day global-period timeline.',
  'Right knee X-ray 05/18/2026 shows stable post-TKA component alignment without fracture, loosening, dislocation, or hardware complication.',
  'Clinical history: postoperative stiffness after right TKA with flexion plateau around 95 to 100 degrees and extension to 0 degrees.',
  'Conservative treatment: formal supervised physical therapy from 03/02/2026 through 05/18/2026 with active and passive range of motion, strengthening, gait training, edema control, and home exercise program; this 77-day continuous course spans the 12th clinical week of conservative management.',
  'PT documented consecutive ROM measurements on 05/04/2026, 05/11/2026, and 05/18/2026 remained static at 95 to 100 degrees of flexion and 0 degrees extension with limited additional expected gains from continued therapy alone.',
  'Functional limitation: this fixed flexion contracture prevents normal reciprocal stair climbing and rising from a standard seated position without assistance, creating a severe ADL impairment despite compliant therapy.',
  'Orthopedic follow-up on 05/18/2026 documents failed conservative treatment and medical necessity for manipulation under anesthesia.',
  'Medical clearance on 05/18/2026 reviewed type 2 diabetes, hypertension, dyslipidemia, CAD, sleep apnea, hypothyroidism, and BMI 36.4 with no acute contraindication.',
  'Hospital outpatient site-of-care addendum: due to coronary artery disease, hypertension, type 2 diabetes, BMI 36.4, and sleep apnea requiring post-anesthetic airway and cardiopulmonary monitoring, an ambulatory surgery center is clinically contraindicated and St. Elizabeth Florence hospital outpatient setting is medically necessary.',
  'BMI 36.4 is below the BMI greater than 40 threshold that would extend conservative therapy to 24 weeks.',
  'HbA1c laboratory report dated 05/18/2026 documents HbA1c 7.2%, below the 8% threshold.',
];

const ackmanPostOpFacts = [
  'Operative report on 05/28/2026 documents manipulation of the right knee joint under general anesthesia, CPT 27570, restoration of motion from 0 to 130 degrees, and no complications.',
];

const ackmanPreAuthSummary = ackmanPreAuthFacts.join(' ');
const ackmanClinicalSummary = [...ackmanPreAuthFacts, ...ackmanPostOpFacts].join(' ');

const brownPreAuthFacts = [
  'Patient: Linda Brown. DOB 12/02/1950. Sex female. MRN 01278289. CSN 1204005349.',
  'Date of service 05/20/2026 11:00 AM at St. Elizabeth Florence.',
  'Surgeon John J. Larkin, MD. Payer Humana.',
  'Primary procedure: Primary repair of traumatic left quadriceps/extensor mechanism tendon rupture. CPT 27385.',
  'Diagnosis: Traumatic tear of quadriceps tendon and VMO of left knee; status post left total knee arthroplasty performed 04/08/2026.',
  'Index left TKA source record dated 04/08/2026 confirms prior left total knee arthroplasty and establishes the post-arthroplasty traumatic timeline.',
  'Clinical history: fall approximately six weeks after left TKA with pain, swelling, weakness, extensor lag, and difficulty performing straight leg raise.',
  'Physical exam documents MRC strength 1/5 for active straight-leg raise and knee extension, with extensor lag confirming objective motor deficit.',
  'Urgent orthopedic evaluation on 05/18/2026 documents acute extensor mechanism injury and urgent surgical need.',
  'MRI left knee on 05/18/2026 confirms disruption of distal quadriceps tendon fibers and VMO insertion with edema and hematoma.',
  'X-ray left knee on 05/18/2026 shows total knee arthroplasty components without acute periprosthetic fracture and anterior soft tissue swelling.',
  'Medical clearance on 05/19/2026 documents stable cardiopulmonary status and no acute contraindication to urgent hospital outpatient extensor mechanism repair.',
  'Urgent traumatic indication supports conservative-care bypass because delaying repair risks persistent extensor mechanism failure.',
];

const brownPostOpFacts = [
  'Operative report on 05/20/2026 documents primary repair of traumatic left quadriceps/extensor mechanism tendon rupture, CPT 27385, with repair of quadriceps and VMO tendon disruption.',
];

const brownPreAuthSummary = brownPreAuthFacts.join(' ');
const brownClinicalSummary = [...brownPreAuthFacts, ...brownPostOpFacts].join(' ');

const ackmanDocs: OrthopedicPacketDoc[] = [
  {
    type: 'Combined Pre-Authorization Packet',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-18T10:30:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/combined_patient_packets/ackman-patient-packet.pdf',
    extracted_text: ackmanPreAuthSummary,
  },
  {
    type: 'Operative Report - Right Knee Manipulation',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-28T15:00:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/individual_supporting_documents/ackman/ackman-operative-report-right-knee-manipulation.pdf',
    extracted_text:
      'Operative report 05/28/2026: manipulation of right knee joint under general anesthesia for arthrofibrosis after right TKA. CPT 27570. Right knee motion restored to 0 to 130 degrees without complication.',
  },
  {
    type: 'Index Right TKA Source Record',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-18T08:00:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/individual_supporting_documents/ackman/ackman-index-right-tka-source-record.pdf',
    extracted_text:
      'Index right TKA source record 02/18/2026: primary right total knee arthroplasty completed without documented surgical complication. This establishes the 90-day global-period timeline for the later right knee manipulation request.',
  },
  {
    type: 'X-Ray Right Knee - Post-TKA Alignment',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-18T08:15:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/individual_supporting_documents/ackman/ackman-x-ray-right-knee-post-tka-alignment.pdf',
    extracted_text:
      'X-ray right knee 05/18/2026: stable total knee arthroplasty component alignment. No fracture, loosening, dislocation, hardware complication, or periprosthetic lucency. Findings support stiffness/arthrofibrosis rather than implant failure.',
  },
  {
    type: 'Orthopedic Follow-Up Note - Post-TKA Stiffness',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-18T09:30:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/individual_supporting_documents/ackman/ackman-orthopedic-follow-up-note-post-tka-stiffness.pdf',
    extracted_text:
      'Orthopedic follow-up 05/18/2026: status post right total knee arthroplasty 02/18/2026 with persistent stiffness and arthrofibrosis. Range of motion plateau around 0 to 100 degrees despite therapy. The fixed flexion limitation prevents normal reciprocal stair climbing and rising from a standard seated position without assistance. Manipulation under anesthesia recommended as medically necessary.',
  },
  {
    type: 'Physical Therapy Progress Note - Range of Motion Plateau',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-18T08:30:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/individual_supporting_documents/ackman/ackman-physical-therapy-progress-note-range-of-motion-plateau.pdf',
    extracted_text:
      'PT progress note 05/18/2026: formal supervised in-person physical therapy 2 to 3 times weekly from 03/02/2026 through 05/18/2026 with active and passive range of motion, strengthening, gait training, edema control, and home exercise program. This 77-day continuous course spans the 12th clinical week of conservative management. Consecutive measurements on 05/04/2026, 05/11/2026, and 05/18/2026 remained static with flexion plateau 95 to 100 degrees and extension 0 degrees. Functional limitation persists: patient cannot perform reciprocal stairs or rise from a standard chair without assistance.',
  },
  {
    type: 'Medical Clearance Summary - Right Knee Manipulation',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-18T10:00:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/individual_supporting_documents/ackman/ackman-medical-clearance-summary-right-knee-manipulation.pdf',
    extracted_text:
      'Medical clearance 05/18/2026: type 2 diabetes, hypertension, dyslipidemia, coronary artery disease, sleep apnea, hypothyroidism, and BMI 36.4 reviewed. BMI 36.4 is below the BMI greater than 40 threshold that would extend conservative therapy to 24 weeks. No acute medical contraindication to right knee manipulation under anesthesia. Due to coronary artery disease, hypertension, type 2 diabetes, BMI 36.4, and sleep apnea requiring post-anesthetic airway and cardiopulmonary monitoring, an ambulatory surgery center is clinically contraindicated. The hospital outpatient setting at St. Elizabeth Florence is medically necessary to mitigate post-anesthetic thromboembolic, airway, and cardiac risk.',
  },
  {
    type: 'HbA1c Laboratory Report - Glycemic Control',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-18T10:05:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/individual_supporting_documents/ackman/ackman-hba1c-laboratory-report-glycemic-control.pdf',
    extracted_text:
      'HbA1c laboratory report 05/18/2026: Hemoglobin A1c 7.2%, below the payer glycemic-control threshold of 8% for diabetic peri-anesthetic risk mitigation.',
  },
];

const brownDocs: OrthopedicPacketDoc[] = [
  {
    type: 'Combined Pre-Authorization Packet',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-18T16:00:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/combined_patient_packets/brown-patient-packet.pdf',
    extracted_text: brownPreAuthSummary,
  },
  {
    type: 'Operative Report - Left Extensor Mechanism Repair',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-20T16:00:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/individual_supporting_documents/brown/brown-operative-report-left-extensor-mechanism-repair.pdf',
    extracted_text:
      'Operative report 05/20/2026: primary repair of traumatic left quadriceps/extensor mechanism tendon rupture with repair of quadriceps tendon and VMO tendon. CPT 27385. Traumatic tear after left total knee arthroplasty.',
  },
  {
    type: 'Index Left TKA Source Record',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-18T13:45:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/individual_supporting_documents/brown/brown-index-left-tka-source-record.pdf',
    extracted_text:
      'Index left TKA source record 04/08/2026: prior left total knee arthroplasty documented before the fall-related extensor mechanism injury. This establishes the post-arthroplasty traumatic context and rules out primary degenerative pain as the surgical indication.',
  },
  {
    type: 'Urgent Orthopedic Evaluation - Left Knee Extensor Mechanism Injury',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-18T15:00:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/individual_supporting_documents/brown/brown-urgent-orthopedic-evaluation-left-knee-extensor-mechanism-injury.pdf',
    extracted_text:
      'Urgent orthopedic evaluation 05/18/2026: fall after left TKA with pain, swelling, weakness, extensor lag, and difficulty straight leg raise. MRC strength is 1/5 for active straight-leg raise and knee extension. Acute extensor mechanism disruption suspected. Urgent operative repair recommended.',
  },
  {
    type: 'MRI Left Knee - Extensor Mechanism Disruption',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-18T14:30:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/individual_supporting_documents/brown/brown-mri-left-knee-extensor-mechanism-disruption.pdf',
    extracted_text:
      'MRI left knee 05/18/2026: disruption of distal quadriceps tendon fibers and VMO insertional disruption with edema and hematoma, supporting traumatic extensor mechanism injury.',
  },
  {
    type: 'X-Ray Left Knee - Post-TKA Fall Evaluation',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-18T14:00:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/individual_supporting_documents/brown/brown-x-ray-left-knee-post-tka-fall-evaluation.pdf',
    extracted_text:
      'X-ray left knee 05/18/2026: total knee arthroplasty components without acute periprosthetic fracture. Anterior soft tissue swelling after fall.',
  },
  {
    type: 'Medical Clearance - Urgent Left Extensor Mechanism Repair',
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: '2026-05-19T08:30:00.000Z',
    source_path:
      '/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/individual_supporting_documents/brown/brown-medical-clearance-urgent-left-extensor-mechanism-repair.pdf',
    extracted_text:
      'Medical clearance 05/19/2026: 75-year-old female with stable cardiopulmonary status after recent left TKA. No acute medical contraindication to urgent hospital outpatient repair of traumatic left extensor mechanism disruption. DVT prophylaxis and post-anesthetic monitoring plan reviewed.',
  },
];

function isCombinedPreAuthPacket(doc: OrthopedicPacketDoc): boolean {
  return /combined pre-authorization packet/i.test(doc.type);
}

function isOperativeReport(doc: OrthopedicPacketDoc): boolean {
  return /operative report/i.test(doc.type);
}

function getRawPreAuthDocs(docs: OrthopedicPacketDoc[]): OrthopedicPacketDoc[] {
  return docs.filter((doc) => !isCombinedPreAuthPacket(doc) && !isOperativeReport(doc));
}

function getRawPostOpDocs(docs: OrthopedicPacketDoc[]): OrthopedicPacketDoc[] {
  return docs.filter((doc) => !isCombinedPreAuthPacket(doc));
}

function makeSyntheticDoc(params: {
  patientKey: string;
  type: string;
  uploadedAt: string;
  extractedText: string;
}): OrthopedicPacketDoc {
  const slug = params.type
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  return {
    type: params.type,
    status: 'verified',
    uploaded_by: 'LINK Synthetic Packet',
    uploaded_at: params.uploadedAt,
    source_path:
      `/Users/brentgoode/Desktop/ Link/LINK_Synthetic_Test_Patient_Packets/individual_supporting_documents/${params.patientKey}/${params.patientKey}-${slug}.pdf`,
    extracted_text: params.extractedText,
  };
}

function buildSyntheticCase(params: {
  id: string;
  patientName: string;
  patientDob: string;
  patientMrn: string;
  patientSex: ScheduledCase['patient_sex'];
  scheduledDate: string;
  template: SurgeonTemplate;
  encounterId: string;
  payerName: string;
  docs: OrthopedicPacketDoc[];
}): ScheduledCase {
  return {
    id: params.id,
    patient_name: params.patientName,
    patient_dob: params.patientDob,
    patient_mrn: params.patientMrn,
    patient_sex: params.patientSex,
    surgeon_id: DEMO_SURGEON_ID,
    facility_id: DEMO_FACILITY_ID,
    facility_name: ORTHOPEDIC_PACKET_DEMO_FACILITY.name,
    scheduled_date: params.scheduledDate,
    scheduled_procedure: params.template.template_name,
    encounter_id: params.encounterId,
    payer_name: params.payerName,
    plan_name: 'Synthetic Orthopedic Packet',
    member_id: null,
    status: 'scheduled',
    is_demo: true,
    pre_loaded_docs: getRawPreAuthDocs(params.docs),
  };
}

const ackmanTemplate: SurgeonTemplate = {
  id: 'demo-ortho-template-ackman-knee-manipulation',
  surgeon_id: DEMO_SURGEON_ID,
  template_name: 'Manipulation of right knee joint under general anesthesia',
  specialty: 'orthopedics',
  body_region: 'knee',
  laterality_required: true,
  primary_cpts: [
    {
      code: '27570',
      description: 'Manipulation of right knee joint under general anesthesia',
    },
  ],
  secondary_cpts: [],
  narrative_template: [
    'Pre-Authorization Packet',
    '',
    'Patient: {{PATIENT_NAME}}',
    'DOB: {{DOB}}',
    'MRN: {{MRN}}',
    'CSN: {{CSN}}',
    'Date of Service: {{DATE_OF_SERVICE}}',
    'Surgeon: {{SURGEON_NAME}}',
    'Facility: {{FACILITY_NAME}}',
    'Payer: {{PAYER_NAME}}',
    '',
    'Requested procedure:',
    '{{PRIMARY_PROCEDURES}}',
    '',
    'Diagnosis:',
    '{{DIAGNOSIS_LIST}}',
    '',
    'Medical necessity summary:',
    'The record supports medically necessary right knee manipulation under anesthesia for postoperative arthrofibrosis after right total knee replacement. Formal supervised therapy documented a fixed range-of-motion plateau despite continued treatment, and the orthopedic note supports manipulation to restore functional motion.',
  ].join('\n'),
  common_addons: [],
  diagnosis_codes: [
    {
      code: 'M25.661',
      description: 'Stiffness of right knee, not elsewhere classified; arthrofibrosis status post right TKA',
    },
  ],
  metadata: {
    source: 'LINK synthetic orthopedic packet',
    packet_patient: 'Amanda Ackman',
    operating_surgeon_name: 'Dr. John J. Larkin, MD',
    prior_tka_date: '2026-02-18',
    global_period_days: 90,
    default_high_complexity: false,
    assistant_note_enabled_default: false,
  },
  version: 1,
  is_active: true,
  is_demo: true,
  created_at: DEMO_NOW,
  updated_at: DEMO_NOW,
};

const brownTemplate: SurgeonTemplate = {
  id: 'demo-ortho-template-brown-extensor-mechanism-repair',
  surgeon_id: DEMO_SURGEON_ID,
  template_name: 'Reconstruction and repair of extensor mechanism with repair of quadriceps and VMO tendons, left knee',
  specialty: 'orthopedics',
  body_region: 'knee',
  laterality_required: true,
  primary_cpts: [
    {
      code: '27385',
      description: 'Primary repair of traumatic quadriceps/extensor mechanism tendon rupture, left knee',
    },
  ],
  secondary_cpts: [],
  narrative_template: [
    'Pre-Authorization Packet',
    '',
    'Patient: {{PATIENT_NAME}}',
    'DOB: {{DOB}}',
    'MRN: {{MRN}}',
    'CSN: {{CSN}}',
    'Date of Service: {{DATE_OF_SERVICE}}',
    'Surgeon: {{SURGEON_NAME}}',
    'Facility: {{FACILITY_NAME}}',
    'Payer: {{PAYER_NAME}}',
    '',
    'Requested procedure:',
    '{{PRIMARY_PROCEDURES}}',
    '',
    'Diagnosis:',
    '{{DIAGNOSIS_LIST}}',
    '',
    'Medical necessity summary:',
    'The record supports urgent operative repair of traumatic left knee extensor mechanism disruption after total knee arthroplasty. MRI confirms distal quadriceps tendon and VMO insertional disruption, and the urgent evaluation documents extensor lag and impaired straight-leg raise. This acute traumatic indication supports bypass of routine conservative-care timing.',
  ].join('\n'),
  common_addons: [],
  diagnosis_codes: [
    {
      code: 'S76.112A',
      description: 'Traumatic tear of left quadriceps tendon and VMO; initial encounter',
    },
  ],
  metadata: {
    source: 'LINK synthetic orthopedic packet',
    packet_patient: 'Linda Brown',
    operating_surgeon_name: 'Dr. John J. Larkin, MD',
    urgent_bypass: true,
    urgent_bypass_reason: 'Traumatic extensor mechanism disruption confirmed by MRI',
    default_high_complexity: false,
    assistant_note_enabled_default: false,
  },
  version: 1,
  is_active: true,
  is_demo: true,
  created_at: DEMO_NOW,
  updated_at: DEMO_NOW,
};

const ackmanCase: ScheduledCase = {
  id: 'demo-ortho-scheduled-ackman',
  patient_name: 'Amanda Ackman',
  patient_dob: '1969-12-18',
  patient_mrn: '00348504',
  patient_sex: 'female',
  surgeon_id: DEMO_SURGEON_ID,
  facility_id: DEMO_FACILITY_ID,
  facility_name: ORTHOPEDIC_PACKET_DEMO_FACILITY.name,
  scheduled_date: '2026-05-28T08:30:00-04:00',
  scheduled_procedure: ackmanTemplate.template_name,
  encounter_id: '1203557652',
  payer_name: 'Anthem BCBS',
  plan_name: 'Synthetic Orthopedic Packet',
  member_id: null,
  status: 'scheduled',
  is_demo: true,
  pre_loaded_docs: getRawPreAuthDocs(ackmanDocs),
};

const brownCase: ScheduledCase = {
  id: 'demo-ortho-scheduled-brown',
  patient_name: 'Linda Brown',
  patient_dob: '1950-12-02',
  patient_mrn: '01278289',
  patient_sex: 'female',
  surgeon_id: DEMO_SURGEON_ID,
  facility_id: DEMO_FACILITY_ID,
  facility_name: ORTHOPEDIC_PACKET_DEMO_FACILITY.name,
  scheduled_date: '2026-05-20T11:00:00-04:00',
  scheduled_procedure: brownTemplate.template_name,
  encounter_id: '1204005349',
  payer_name: 'Humana',
  plan_name: 'Synthetic Orthopedic Packet',
  member_id: null,
  status: 'scheduled',
  is_demo: true,
  pre_loaded_docs: getRawPreAuthDocs(brownDocs),
};

const sarahJenkinsInitialEvaluationText = [
  'LINK ORTHOPEDICS AND SPORTS MEDICINE',
  'Initial Orthopedic Evaluation and History & Physical',
  'Encounter Date: 05/26/2026',
  'Patient: Sarah Jenkins',
  'DOB: 08/14/1968',
  'MRN: DEMO-RC-2045',
  'CSN: DEMO-RC-9045',
  'Payer: Anthem BCBS / Carelon pathway',
  'Provider: John J. Larkin, MD',
  'Chief Complaint: Right shoulder pain and weakness.',
  '',
  'History of Present Illness:',
  'Sarah Jenkins is a 57-year-old right-hand dominant female evaluated for exactly 6 months of progressively worsening right shoulder pain beginning 11/26/2025. Pain started without acute fracture or dislocation and has progressed despite activity modification. She reports severe nocturnal pain interrupting sleep 5 to 6 nights per week and inability to sleep on the right side. Pain is 8/10 with overhead activity and 6/10 at rest. She is unable to reach above shoulder height, fasten a bra, put on a jacket normally, wash or style her hair, lift dishes into an overhead cabinet, or perform sustained work at shoulder level.',
  '',
  'Prior Treatment:',
  'The patient completed a 6-week trial of Meloxicam 15 mg by mouth once daily from 12/02/2025 through 01/13/2026. Medication was taken as prescribed with food and discontinued due to lack of efficacy. She has also attempted rest, ice, activity modification, home exercise, supervised physical therapy, and subacromial corticosteroid injection without sustained functional improvement.',
  '',
  'Medical History:',
  'Patient has Severe Obstructive Sleep Apnea (OSA) requiring continuous CPAP, and a BMI of 41. Due to high risk of airway compromise, general anesthesia in an Ambulatory Surgery Center is strictly contraindicated. Patient must be treated in a hospital-based outpatient setting.',
  '',
  'Physical Examination:',
  'Right shoulder inspection shows no erythema, open wound, or deformity. Tenderness is present over the anterolateral cuff footprint and bicipital groove. Active Range of Motion (AROM): forward flexion 82 degrees, abduction 74 degrees, external rotation at side 28 degrees, internal rotation to right buttock. Passive Range of Motion (PROM): forward flexion 142 degrees, abduction 136 degrees, external rotation at side 58 degrees, internal rotation to L4. Pain is reproduced with resisted elevation. Strength: external rotation 3/5, supraspinatus 3/5, abduction 3+/5, internal rotation 4/5. Jobe\'s Empty Can test is positive. Hawkins-Kennedy impingement sign is positive. Neer sign is positive. Speed test is positive. Distal motor and sensory exam is intact in median, radial, ulnar, and axillary distributions. Radial pulse is 2+.',
  '',
  'Imaging Reviewed:',
  'Right shoulder X-ray dated 05/12/2026 demonstrates type III hooked acromion with subacromial spurring and decreased acromiohumeral interval. MRI right shoulder without contrast dated 05/16/2026 demonstrates full-thickness full-width supraspinatus tear with 2 cm medial retraction, high-grade partial infraspinatus tear, long head biceps tendon subluxation/tendinosis, and severe subacromial/subdeltoid bursitis.',
  '',
  'Assessment:',
  '1. Complete rotator cuff tear or rupture of right shoulder, not specified as traumatic. ICD-10 M75.111.',
  '2. Right shoulder impingement due to type III hooked acromion and subacromial spurring.',
  '3. Long head biceps tendon subluxation and tendinosis, right shoulder.',
  '',
  'Plan:',
  'Conservative treatment has failed. The requested procedure is arthroscopic rotator cuff repair, right shoulder, CPT 29827-RT, with arthroscopic subacromial decompression/acromioplasty, CPT 29826-XS, and arthroscopic biceps tenodesis, CPT 29828-XS. CPT 29826 is requested as an add-on-style secondary shoulder decompression code tied to separate subacromial outlet pathology. The subacromial decompression addresses the separate subacromial space/type III acromion outlet impingement, and biceps tenodesis addresses separate long-head biceps instability/tendinosis. The patient understands risks, benefits, alternatives, and expected rehabilitation timeline.',
].join('\n');

const sarahJenkinsPtDischargeText = [
  'FLORENCE ORTHOPEDIC REHABILITATION',
  'Physical Therapy Discharge Summary',
  'Patient: Sarah Jenkins',
  'DOB: 08/14/1968',
  'MRN: DEMO-RC-2045',
  'Referring Provider: John J. Larkin, MD',
  'Diagnosis: Complete rotator cuff tear or rupture of right shoulder, M75.111',
  'Therapy Start Date: 02/26/2026',
  'Therapy End Date: 04/23/2026',
  'Discharge Summary Date: 04/26/2026',
  '',
  'Attendance:',
  'Patient attended formal, in-person, supervised physical therapy 2 times per week for exactly 8 weeks, for a total of 16 supervised sessions. Attendance dates: 02/26/2026, 03/02/2026, 03/05/2026, 03/09/2026, 03/12/2026, 03/16/2026, 03/19/2026, 03/23/2026, 03/26/2026, 03/30/2026, 04/02/2026, 04/06/2026, 04/09/2026, 04/13/2026, 04/16/2026, and 04/23/2026.',
  '',
  'Treatment Provided:',
  'Supervised therapeutic exercise, scapular stabilization, rotator cuff isometrics, active-assisted range of motion, passive stretching, posterior capsule mobility, manual therapy, postural training, home exercise progression, cryotherapy, and activity modification education.',
  '',
  'Objective Measures:',
  'Initial AROM on 02/26/2026: forward flexion 78 degrees, abduction 70 degrees, external rotation 26 degrees. Discharge AROM on 04/23/2026: forward flexion 84 degrees, abduction 76 degrees, external rotation 30 degrees. PROM improved only modestly from forward flexion 138 degrees to 144 degrees. Strength remained limited at external rotation 3/5 and supraspinatus 3/5. Pain remained 7/10 to 8/10 with overhead activity and 6/10 at night.',
  '',
  'Discharge Assessment:',
  'Patient has reached a plateau. Despite 16 supervised sessions, patient continues to have severe pain with overhead activities and is unable to perform basic Activities of Daily Living (ADLs) including dressing and reaching above shoulder height. Conservative therapy has failed.',
  '',
  'Recommendation:',
  'Discharge from formal PT due to failed conservative response and plateau. Patient is independent with home exercise program but remains functionally limited and is referred back to orthopedic surgeon for definitive management.',
].join('\n');

const sarahJenkinsInjectionText = [
  'LINK ORTHOPEDICS AND SPORTS MEDICINE',
  'Interventional Procedure Note - Right Shoulder Injection',
  'Date of Procedure: 03/17/2026',
  'Patient: Sarah Jenkins',
  'DOB: 08/14/1968',
  'MRN: DEMO-RC-2045',
  'Provider: John J. Larkin, MD',
  '',
  'Procedure:',
  'Right shoulder subacromial corticosteroid injection.',
  '',
  'Indication:',
  'Persistent right shoulder pain with impingement signs and suspected rotator cuff pathology after medication and activity modification.',
  '',
  'Technique:',
  'After informed consent and standard timeout, the right posterior subacromial space was prepped with chlorhexidine. Using sterile technique, a 22-gauge needle was advanced into the subacromial space. Injected 40 mg triamcinolone acetonide with 4 mL 1% lidocaine. Patient tolerated procedure without complication.',
  '',
  'Response:',
  'Patient reported only 15% pain relief lasting for 4 days before returning to baseline severe pain. Injection trial failed to provide sustained functional improvement.',
].join('\n');

const sarahJenkinsXrayText = [
  'ST. ELIZABETH FLORENCE RADIOLOGY',
  'Right Shoulder X-Ray Report',
  'Exam Date: 05/12/2026',
  'Patient: Sarah Jenkins',
  'DOB: 08/14/1968',
  'MRN: DEMO-RC-2045',
  '',
  'Exam:',
  'Right shoulder radiographs, minimum 3 views.',
  '',
  'Findings:',
  'Type III hooked acromial morphology is present. Subacromial spurring is noted. Acromiohumeral interval is decreased. Mild glenohumeral osteoarthritic change is present. No acute fracture. No dislocation. Visualized right hemithorax is unremarkable.',
  '',
  'Impression:',
  'Type III hooked acromion with subacromial spurring and decreased acromiohumeral interval. No acute fracture or dislocation. Mild glenohumeral osteoarthritis.',
].join('\n');

const sarahJenkinsMriText = [
  'ST. ELIZABETH FLORENCE RADIOLOGY',
  'MRI Right Shoulder Without Contrast',
  'Exam Date: 05/16/2026',
  'Patient: Sarah Jenkins',
  'DOB: 08/14/1968',
  'MRN: DEMO-RC-2045',
  '',
  'Technique:',
  'Multiplanar multisequence MRI of the right shoulder performed without intravenous contrast.',
  '',
  'Findings:',
  'There is a full-thickness, full-width tear of the supraspinatus tendon with approximately 2 cm medial retraction from the greater tuberosity footprint. There is high-grade partial-thickness tearing of the infraspinatus tendon involving the articular and interstitial fibers. Subscapularis tendinosis is present without full-thickness tear. The long head of the biceps tendon demonstrates tendinosis with medial subluxation at the bicipital groove. Severe subacromial/subdeltoid bursitis is present. Mild glenohumeral osteoarthritis is present. No acute fracture or marrow replacing lesion.',
  '',
  'Impression:',
  '1. Full-thickness, full-width tear of the supraspinatus tendon with 2cm medial retraction. 2. High-grade partial tear of the infraspinatus. 3. Subluxation and tendinosis of the long head of the biceps tendon. 4. Severe subacromial/subdeltoid bursitis.',
].join('\n');

const sarahJenkinsPreAuthFacts = [
  'Patient: Sarah Jenkins. DOB 08/14/1968. Sex female. MRN DEMO-RC-2045. CSN DEMO-RC-9045.',
  'Date of service 06/12/2026 07:30 AM at St. Elizabeth Florence.',
  'Surgeon John J. Larkin, MD. Payer Anthem BCBS.',
  'Primary procedure: Arthroscopic rotator cuff repair, right shoulder. CPT 29827.',
  'Secondary procedures: Arthroscopic subacromial decompression/acromioplasty, CPT 29826, and arthroscopic biceps tenodesis, CPT 29828.',
  'Diagnosis: Complete rotator cuff tear or rupture of right shoulder. ICD-10 M75.111.',
  'Initial orthopedic evaluation on 05/26/2026 documents exactly 6 months of progressively worsening right shoulder pain beginning 11/26/2025, severe nocturnal pain interrupting sleep, failed 6-week Meloxicam 15 mg daily trial, AROM/PROM restriction, 3/5 external rotation weakness, positive Jobe Empty Can, and positive Hawkins-Kennedy impingement sign.',
  'Medical history documents Severe Obstructive Sleep Apnea (OSA) requiring continuous CPAP, BMI of 41, high risk of airway compromise, and Ambulatory Surgery Center contraindication requiring hospital-based outpatient treatment.',
  'Physical therapy discharge summary documents formal in-person supervised physical therapy from 02/26/2026 through 04/23/2026, 2 times weekly for exactly 8 weeks and 16 supervised sessions, with plateau and persistent ADL loss.',
  'Injection note dated 03/17/2026 documents right subacromial corticosteroid injection with only 15% pain relief lasting 4 days before return to baseline severe pain.',
  'X-ray right shoulder 05/12/2026 documents type III hooked acromion with subacromial spurring and decreased acromiohumeral interval.',
  'MRI right shoulder without contrast 05/16/2026 documents full-thickness full-width supraspinatus tear with 2 cm medial retraction, high-grade partial infraspinatus tear, biceps tendon subluxation/tendinosis, and severe subacromial/subdeltoid bursitis.',
  'Distinct-procedure proof: rotator cuff repair addresses the torn supraspinatus/infraspinatus cuff footprint; CPT 29826 addresses the separate subacromial outlet/type III acromion impingement space; CPT 29828 addresses separate long-head biceps instability/tendinosis. Distinct anatomic structures support XS modifier usage for separately reportable secondary work when clinically appropriate.',
];
const sarahJenkinsPostOpFacts = [
  'Operative report 06/12/2026 documents right shoulder arthroscopic rotator cuff repair, arthroscopic subacromial decompression/acromioplasty, and arthroscopic biceps tenodesis. CPTs 29827, 29826, and 29828. No complications.',
];
const sarahJenkinsClinicalSummary = [...sarahJenkinsPreAuthFacts, ...sarahJenkinsPostOpFacts].join(' ');
const sarahJenkinsDocs: OrthopedicPacketDoc[] = [
  makeSyntheticDoc({
    patientKey: 'sarah-jenkins',
    type: 'Initial Orthopedic Evaluation and H&P - Right Shoulder',
    uploadedAt: '2026-05-26T13:30:00.000Z',
    extractedText: sarahJenkinsInitialEvaluationText,
  }),
  makeSyntheticDoc({
    patientKey: 'sarah-jenkins',
    type: 'Physical Therapy Discharge Summary - Right Shoulder Failed Conservative Care',
    uploadedAt: '2026-04-26T14:00:00.000Z',
    extractedText: sarahJenkinsPtDischargeText,
  }),
  makeSyntheticDoc({
    patientKey: 'sarah-jenkins',
    type: 'Injection History - Right Subacromial Corticosteroid',
    uploadedAt: '2026-03-17T15:00:00.000Z',
    extractedText: sarahJenkinsInjectionText,
  }),
  makeSyntheticDoc({
    patientKey: 'sarah-jenkins',
    type: 'X-Ray Right Shoulder - Outlet Impingement',
    uploadedAt: '2026-05-12T11:20:00.000Z',
    extractedText: sarahJenkinsXrayText,
  }),
  makeSyntheticDoc({
    patientKey: 'sarah-jenkins',
    type: 'MRI Report - Right Shoulder Without Contrast',
    uploadedAt: '2026-05-16T12:00:00.000Z',
    extractedText: sarahJenkinsMriText,
  }),
  makeSyntheticDoc({
    patientKey: 'sarah-jenkins',
    type: 'Operative Report - Right Shoulder Rotator Cuff Repair',
    uploadedAt: '2026-06-12T16:00:00.000Z',
    extractedText: sarahJenkinsPostOpFacts.join(' '),
  }),
];
const sarahJenkinsTemplate: SurgeonTemplate = {
  id: 'demo-ortho-template-sarah-jenkins-right-rotator-cuff',
  surgeon_id: DEMO_SURGEON_ID,
  template_name: 'Arthroscopic rotator cuff repair, right shoulder',
  specialty: 'orthopedics',
  body_region: 'shoulder',
  laterality_required: true,
  primary_cpts: [
    { code: '29827', description: 'Arthroscopic rotator cuff repair, right shoulder' },
  ],
  secondary_cpts: [
    { code: '29826', description: 'Arthroscopic subacromial decompression/acromioplasty, right shoulder', default_modifiers: ['XS'] },
    { code: '29828', description: 'Arthroscopic biceps tenodesis, right shoulder', default_modifiers: ['XS'] },
  ],
  narrative_template: null,
  common_addons: [
    { code: '29826', description: 'Arthroscopic subacromial decompression/acromioplasty add-on-style shoulder code' },
  ],
  diagnosis_codes: [
    { code: 'M75.111', description: 'Complete rotator cuff tear or rupture of right shoulder, not specified as traumatic' },
  ],
  metadata: {
    source: 'LINK synthetic orthopedic packet',
    packet_patient: 'Sarah Jenkins',
    operating_surgeon_name: 'Dr. John J. Larkin, MD',
    ncci_distinct_structures: ['rotator cuff footprint', 'subacromial outlet', 'long-head biceps'],
    add_on_cpts: ['29826'],
    default_high_complexity: true,
    assistant_note_enabled_default: false,
  },
  version: 1,
  is_active: true,
  is_demo: true,
  created_at: DEMO_NOW,
  updated_at: DEMO_NOW,
};
const sarahJenkinsCase = buildSyntheticCase({
  id: 'demo-ortho-scheduled-sarah-jenkins-rotator-cuff',
  patientName: 'Sarah Jenkins',
  patientDob: '1968-08-14',
  patientMrn: 'DEMO-RC-2045',
  patientSex: 'female',
  scheduledDate: '2026-06-12T07:30:00-04:00',
  template: sarahJenkinsTemplate,
  encounterId: 'DEMO-RC-9045',
  payerName: 'Anthem BCBS',
  docs: sarahJenkinsDocs,
});

const palmerPreAuthFacts = [
  'Patient: Thomas Palmer. DOB 07/15/1945. Sex male. MRN DEMO-SH-1002. CSN DEMO-SH-9002.',
  'Date of service 06/08/2026 08:30 AM at St. Elizabeth Florence.',
  'Surgeon John J. Larkin, MD. Payer UnitedHealthcare.',
  'Primary procedure: open repair of chronic left supraspinatus rotator cuff tear with acromioplasty/subacromial decompression. CPT 23420.',
  'Secondary procedure: distal clavicle resection/acromioclavicular joint excision, left shoulder. CPT 23120.',
  'MRI left shoulder 05/11/2026 documents chronic full-thickness supraspinatus tear with retraction, impingement morphology, and advanced AC joint arthrosis.',
  'X-ray left shoulder 05/11/2026 confirms AC joint arthrosis and outlet impingement morphology without acute fracture.',
  'Orthopedic evaluation documents recurrent pain after prior cuff repair, night pain, overhead weakness, positive impingement signs, and no glenohumeral instability or labral tear requiring labral repair.',
  'Conservative treatment included more than 12 weeks of supervised therapy, activity modification, injection, and medication management without durable improvement.',
  'NCCI distinct-procedure proof: distal clavicle excision treats a separate symptomatic AC joint pain generator from the rotator cuff repair/subacromial decompression.',
];
const palmerPostOpFacts = [
  'Operative report 06/08/2026 documents left shoulder rotator cuff repair with acromioplasty/subacromial decompression and distal clavicle resection. CPTs 23420 and 23120. No complications.',
];
const palmerClinicalSummary = [...palmerPreAuthFacts, ...palmerPostOpFacts].join(' ');
const palmerDocs: OrthopedicPacketDoc[] = [
  makeSyntheticDoc({
    patientKey: 'palmer',
    type: 'MRI Left Shoulder - Chronic Rotator Cuff Tear',
    uploadedAt: '2026-05-11T11:00:00.000Z',
    extractedText:
      'MRI left shoulder 05/11/2026: chronic full-thickness supraspinatus tear with retraction, subacromial impingement morphology, bursal inflammation, and severe AC joint arthrosis. No acute fracture and no labral instability lesion.',
  }),
  makeSyntheticDoc({
    patientKey: 'palmer',
    type: 'X-Ray Left Shoulder - AC Arthrosis and Outlet Impingement',
    uploadedAt: '2026-05-11T10:30:00.000Z',
    extractedText:
      'X-ray left shoulder 05/11/2026: severe acromioclavicular arthrosis, distal clavicle osteophytes, type II acromial outlet morphology, preserved glenohumeral joint space, and no acute fracture.',
  }),
  makeSyntheticDoc({
    patientKey: 'palmer',
    type: 'Orthopedic Evaluation - Left Rotator Cuff Repair',
    uploadedAt: '2026-05-18T09:30:00.000Z',
    extractedText:
      'Orthopedic evaluation 05/18/2026: recurrent left shoulder pain after prior cuff repair with night pain, weakness, positive Hawkins and Neer signs, cross-body adduction pain, and failed conservative care. Surgery recommended for rotator cuff repair and separate distal clavicle excision. NCCI distinct-structure proof: distal clavicle excision treats a separate acromioclavicular joint structure and separate AC joint pain generator from the rotator cuff repair/subacromial outlet work.',
  }),
  makeSyntheticDoc({
    patientKey: 'palmer',
    type: 'Conservative Care Summary - Left Shoulder',
    uploadedAt: '2026-05-18T09:00:00.000Z',
    extractedText:
      'Conservative care summary: supervised shoulder therapy 02/17/2026 through 05/14/2026, home exercise program, corticosteroid injection, activity modification, and NSAIDs when tolerated. Symptoms persisted with weakness and night pain.',
  }),
  makeSyntheticDoc({
    patientKey: 'palmer',
    type: 'Medical Clearance - Left Shoulder Cuff Repair',
    uploadedAt: '2026-05-26T10:00:00.000Z',
    extractedText:
      'Medical clearance 05/26/2026: BMI 29.8, hypertension controlled, no diabetes, no nicotine use, and no acute contraindication to hospital outpatient shoulder surgery. Hospital outpatient site of care is medically necessary because revision open shoulder repair with separate distal clavicle excision requires hospital-level anesthesia monitoring, implant readiness, and immediate peri-anesthetic support; a freestanding ambulatory surgery center is not appropriate for this complex revision shoulder case.',
  }),
  makeSyntheticDoc({
    patientKey: 'palmer',
    type: 'Operative Report - Left Rotator Cuff Repair and DCR',
    uploadedAt: '2026-06-08T15:30:00.000Z',
    extractedText: palmerPostOpFacts.join(' '),
  }),
];
const palmerTemplate: SurgeonTemplate = {
  id: 'demo-ortho-template-palmer-left-rotator-cuff-dcr',
  surgeon_id: DEMO_SURGEON_ID,
  template_name: 'Left rotator cuff repair with acromioplasty and distal clavicle excision',
  specialty: 'orthopedics',
  body_region: 'shoulder',
  laterality_required: true,
  primary_cpts: [
    { code: '23420', description: 'Open repair of chronic rotator cuff tear with acromioplasty, left shoulder' },
  ],
  secondary_cpts: [
    { code: '23120', description: 'Distal clavicle resection/acromioclavicular joint excision, left shoulder', default_modifiers: ['XS'] },
  ],
  narrative_template: null,
  common_addons: [],
  diagnosis_codes: [
    { code: 'M75.122', description: 'Complete rotator cuff tear or rupture of left shoulder' },
    { code: 'M19.012', description: 'Primary osteoarthritis, left shoulder/acromioclavicular joint' },
  ],
  metadata: {
    source: 'LINK synthetic orthopedic packet',
    packet_patient: 'Thomas Palmer',
    operating_surgeon_name: 'Dr. John J. Larkin, MD',
    ncci_distinct_structures: ['rotator cuff outlet', 'acromioclavicular joint'],
    default_high_complexity: false,
    assistant_note_enabled_default: false,
  },
  version: 1,
  is_active: true,
  is_demo: true,
  created_at: DEMO_NOW,
  updated_at: DEMO_NOW,
};
const palmerCase = buildSyntheticCase({
  id: 'demo-ortho-scheduled-palmer-rotator-cuff',
  patientName: 'Thomas Palmer',
  patientDob: '1945-07-15',
  patientMrn: 'DEMO-SH-1002',
  patientSex: 'male',
  scheduledDate: '2026-06-08T08:30:00-04:00',
  template: palmerTemplate,
  encounterId: 'DEMO-SH-9002',
  payerName: 'UnitedHealthcare',
  docs: palmerDocs,
});

const vegaPreAuthFacts = [
  'Patient: Elena Vega. DOB 11/27/1968. Sex female. MRN DEMO-HAND-1001. CSN DEMO-HAND-9001.',
  'Date of service 06/01/2026 09:30 AM at St. Elizabeth Florence.',
  'Surgeon John J. Larkin, MD. Payer Anthem BCBS.',
  'Primary procedure: median neuroplasty with release of transverse carpal ligament, left wrist. CPT 64721.',
  'Diagnosis: chronic severe left carpal tunnel syndrome with compression of the median nerve at the transverse carpal ligament.',
  'EMG/NCS 05/05/2026 documents severe left median neuropathy at the wrist with prolonged distal motor latency and sensory conduction slowing.',
  'Clinical exam documents nocturnal paresthesias, numbness in the thumb/index/middle fingers, positive Phalen and Tinel signs, grip weakness, and early thenar fatigue.',
  'Conservative care failed after night splinting, activity modification, NSAIDs when tolerated, and corticosteroid injection on 04/22/2026 with only temporary relief.',
];
const vegaPostOpFacts = [
  'Operative report 06/01/2026 documents left carpal tunnel release with median neuroplasty and complete release of the transverse carpal ligament. CPT 64721. Tourniquet time less than 10 minutes and no complications.',
];
const vegaClinicalSummary = [...vegaPreAuthFacts, ...vegaPostOpFacts].join(' ');
const vegaDocs: OrthopedicPacketDoc[] = [
  makeSyntheticDoc({
    patientKey: 'vega',
    type: 'EMG NCS - Severe Left Carpal Tunnel Syndrome',
    uploadedAt: '2026-05-05T14:00:00.000Z',
    extractedText:
      'EMG/NCS 05/05/2026: severe left median neuropathy at the wrist, prolonged distal motor latency, sensory conduction slowing, and denervation changes consistent with severe carpal tunnel syndrome.',
  }),
  makeSyntheticDoc({
    patientKey: 'vega',
    type: 'Hand Surgery Evaluation - Left Carpal Tunnel',
    uploadedAt: '2026-05-12T09:30:00.000Z',
    extractedText:
      'Hand surgery evaluation 05/12/2026: chronic severe left carpal tunnel syndrome with nocturnal paresthesias, median nerve distribution numbness, positive Phalen and Tinel signs, grip weakness, and early thenar fatigue. Surgical release recommended after failed conservative treatment.',
  }),
  makeSyntheticDoc({
    patientKey: 'vega',
    type: 'Conservative Care Summary - Left Carpal Tunnel',
    uploadedAt: '2026-05-12T09:00:00.000Z',
    extractedText:
      'Conservative care summary: failed night splinting, activity modification, NSAIDs when tolerated, ergonomic changes, and corticosteroid injection on 04/22/2026 with temporary relief only. Symptoms remain functionally limiting.',
  }),
  makeSyntheticDoc({
    patientKey: 'vega',
    type: 'Medical Clearance - Left Carpal Tunnel Release',
    uploadedAt: '2026-05-21T11:00:00.000Z',
    extractedText:
      'Medical clearance 05/21/2026: BMI 27.6, no diabetes, no nicotine use, and no acute contraindication to left carpal tunnel release.',
  }),
  makeSyntheticDoc({
    patientKey: 'vega',
    type: 'Operative Report - Left Carpal Tunnel Release',
    uploadedAt: '2026-06-01T13:00:00.000Z',
    extractedText: vegaPostOpFacts.join(' '),
  }),
];
const vegaTemplate: SurgeonTemplate = {
  id: 'demo-ortho-template-vega-left-carpal-tunnel',
  surgeon_id: DEMO_SURGEON_ID,
  template_name: 'Left carpal tunnel release with median neuroplasty',
  specialty: 'orthopedics',
  body_region: 'hand',
  laterality_required: true,
  primary_cpts: [
    { code: '64721', description: 'Median neuroplasty with release of transverse carpal ligament, left wrist' },
  ],
  secondary_cpts: [],
  narrative_template: null,
  common_addons: [],
  diagnosis_codes: [
    { code: 'G56.02', description: 'Carpal tunnel syndrome, left upper limb' },
  ],
  metadata: {
    source: 'LINK synthetic orthopedic packet',
    packet_patient: 'Elena Vega',
    operating_surgeon_name: 'Dr. John J. Larkin, MD',
    default_high_complexity: false,
    assistant_note_enabled_default: false,
  },
  version: 1,
  is_active: true,
  is_demo: true,
  created_at: DEMO_NOW,
  updated_at: DEMO_NOW,
};
const vegaCase = buildSyntheticCase({
  id: 'demo-ortho-scheduled-vega-carpal-tunnel',
  patientName: 'Elena Vega',
  patientDob: '1968-11-27',
  patientMrn: 'DEMO-HAND-1001',
  patientSex: 'female',
  scheduledDate: '2026-06-01T09:30:00-04:00',
  template: vegaTemplate,
  encounterId: 'DEMO-HAND-9001',
  payerName: 'Anthem BCBS',
  docs: vegaDocs,
});

const wilderPreAuthFacts = [
  'Patient: Patricia Wilder. DOB 09/29/1961. Sex female. MRN DEMO-TKA-1001. CSN DEMO-TKA-9001.',
  'Date of service 06/16/2026 09:00 AM at St. Elizabeth Florence.',
  'Surgeon John J. Larkin, MD. Payer Anthem BCBS.',
  'Primary procedure: left total knee arthroplasty. CPT 27447.',
  'Secondary procedure: posterior capsular release with medial and lateral collateral ligament balancing for flexion-extension gap. CPT 27435-XS is supported when separately documented as distinct contracture-balancing work.',
  'Diagnosis: severe tricompartmental osteoarthritis of the left knee with bone-on-bone medial and patellofemoral disease, fixed flexion contracture, varus alignment, and failure of conservative treatment.',
  'Weight-bearing X-rays 05/14/2026 document Kellgren-Lawrence grade 4 bone-on-bone tricompartmental osteoarthritis with osteophytes, sclerosis, joint-space collapse, and varus deformity.',
  'Functional impairment includes walking tolerance less than one block, stair limitation, night pain, cane use, and difficulty rising from a chair.',
  'Conservative care failed after more than 24 weeks of supervised therapy/home exercise, weight-loss counseling, NSAIDs when tolerated, activity modification, bracing, and corticosteroid/viscosupplement injections.',
  'BMI is 44.0. The packet explicitly documents high-BMI risk mitigation, hospital outpatient/inpatient-capable monitoring, DVT prophylaxis plan, airway plan, and medical clearance.',
  'HbA1c laboratory report documents HbA1c 6.4%, cotinine laboratory report documents cotinine 4 ng/mL, and cardiology clearance shows acceptable perioperative risk.',
];
const wilderPostOpFacts = [
  'Operative report 06/16/2026 documents left total knee arthroplasty, CPT 27447, with posterior capsular release and collateral ligament balancing, CPT 27435-XS, due to fixed flexion contracture. Implant details and no complications documented.',
];
const wilderClinicalSummary = [...wilderPreAuthFacts, ...wilderPostOpFacts].join(' ');
const wilderDocs: OrthopedicPacketDoc[] = [
  makeSyntheticDoc({
    patientKey: 'wilder',
    type: 'Weight Bearing X-Ray Left Knee - End Stage OA',
    uploadedAt: '2026-05-14T10:00:00.000Z',
    extractedText:
      'Weight-bearing X-ray left knee 05/14/2026: Kellgren-Lawrence grade 4 severe tricompartmental osteoarthritis with bone-on-bone medial and patellofemoral joint-space collapse, osteophytes, sclerosis, and varus deformity.',
  }),
  makeSyntheticDoc({
    patientKey: 'wilder',
    type: 'Orthopedic Evaluation - Left Total Knee Arthroplasty',
    uploadedAt: '2026-05-18T11:00:00.000Z',
    extractedText:
      'Orthopedic evaluation 05/18/2026: severe left knee tricompartmental osteoarthritis with fixed flexion contracture, varus alignment, antalgic gait, walking tolerance less than one block, stair limitation, night pain, cane use, and difficulty rising from a chair. Left total knee arthroplasty recommended.',
  }),
  makeSyntheticDoc({
    patientKey: 'wilder',
    type: 'Conservative Care Summary - Left Knee Arthritis',
    uploadedAt: '2026-05-18T10:30:00.000Z',
    extractedText:
      'Conservative care summary: more than 24 weeks of nonoperative treatment, including supervised therapy, home exercise program, weight-loss counseling, NSAIDs when tolerated, activity modification, bracing, corticosteroid injection, and viscosupplementation. Persistent functional limitation despite compliance.',
  }),
  makeSyntheticDoc({
    patientKey: 'wilder',
    type: 'Medical Clearance - High BMI Left Total Knee Arthroplasty',
    uploadedAt: '2026-05-28T14:00:00.000Z',
    extractedText:
      'Medical clearance 05/28/2026: BMI 44.0, hypertension controlled, dyslipidemia, sleep apnea on CPAP, HbA1c 6.4%, cotinine 4 ng/mL, acceptable cardiology risk, DVT prophylaxis plan, airway plan, and hospital monitoring plan. No acute contraindication to left total knee arthroplasty at St. Elizabeth Florence.',
  }),
  makeSyntheticDoc({
    patientKey: 'wilder',
    type: 'HbA1c Laboratory Report - TKA Glycemic Control',
    uploadedAt: '2026-05-28T09:00:00.000Z',
    extractedText:
      'HbA1c laboratory report 05/28/2026: Hemoglobin A1c 6.4%, below the payer glycemic-control threshold of 8% for elective total knee arthroplasty.',
  }),
  makeSyntheticDoc({
    patientKey: 'wilder',
    type: 'Cotinine Laboratory Report - Nicotine Clearance',
    uploadedAt: '2026-05-28T09:15:00.000Z',
    extractedText:
      'Cotinine laboratory report 05/28/2026: cotinine 4 ng/mL, at or below payer threshold and consistent with nicotine clearance before total knee arthroplasty.',
  }),
  makeSyntheticDoc({
    patientKey: 'wilder',
    type: 'Cardiology Clearance - Perioperative Risk',
    uploadedAt: '2026-05-28T11:30:00.000Z',
    extractedText:
      'Cardiology clearance 05/28/2026: stable coronary risk profile, no active ischemic symptoms, acceptable perioperative cardiac risk for hospital outpatient left total knee arthroplasty with post-anesthetic monitoring.',
  }),
  makeSyntheticDoc({
    patientKey: 'wilder',
    type: 'Operative Report - Left Total Knee Arthroplasty',
    uploadedAt: '2026-06-16T16:00:00.000Z',
    extractedText: wilderPostOpFacts.join(' '),
  }),
];
const wilderTemplate: SurgeonTemplate = {
  id: 'demo-ortho-template-wilder-left-tka',
  surgeon_id: DEMO_SURGEON_ID,
  template_name: 'Left total knee arthroplasty for severe tricompartmental osteoarthritis',
  specialty: 'orthopedics',
  body_region: 'knee',
  laterality_required: true,
  primary_cpts: [
    { code: '27447', description: 'Left total knee arthroplasty' },
  ],
  secondary_cpts: [
    { code: '27435', description: 'Posterior capsular release with collateral ligament balancing, left knee', default_modifiers: ['XS'] },
  ],
  narrative_template: null,
  common_addons: [],
  diagnosis_codes: [
    { code: 'M17.12', description: 'Unilateral primary osteoarthritis, left knee' },
    { code: 'M24.562', description: 'Contracture, left knee' },
  ],
  metadata: {
    source: 'LINK synthetic orthopedic packet',
    packet_patient: 'Patricia Wilder',
    operating_surgeon_name: 'Dr. John J. Larkin, MD',
    bmi: 44,
    high_bmi_risk_mitigated: true,
    default_high_complexity: true,
    assistant_note_enabled_default: false,
  },
  version: 1,
  is_active: true,
  is_demo: true,
  created_at: DEMO_NOW,
  updated_at: DEMO_NOW,
};
const wilderCase = buildSyntheticCase({
  id: 'demo-ortho-scheduled-wilder-left-tka',
  patientName: 'Patricia Wilder',
  patientDob: '1961-09-29',
  patientMrn: 'DEMO-TKA-1001',
  patientSex: 'female',
  scheduledDate: '2026-06-16T09:00:00-04:00',
  template: wilderTemplate,
  encounterId: 'DEMO-TKA-9001',
  payerName: 'Anthem BCBS',
  docs: wilderDocs,
});

const nolanPreAuthFacts = [
  'Patient: Helen Nolan. DOB 11/03/1949. Sex female. MRN DEMO-SCOPE-1001. CSN DEMO-SCOPE-9001.',
  'Date of service 06/11/2026 11:00 AM at St. Elizabeth Florence.',
  'Surgeon John J. Larkin, MD. Payer Medicare Advantage.',
  'Primary procedure: right knee arthroscopy with partial medial and lateral meniscectomy. CPT 29880.',
  'Diagnosis: displaced medial meniscus tear, lateral meniscus tear, recurrent mechanical locking/catching, effusion, and grade 2 to 3 chondromalacia without bone-on-bone osteoarthritis.',
  'MRI right knee 05/16/2026 documents displaced posterior horn medial meniscus tear, complex lateral meniscus tear, joint effusion, and grade 2 to 3 patellofemoral chondromalacia.',
  'Clinical exam documents recurrent locking, catching, positive McMurray, joint-line tenderness, effusion, and failure of conservative treatment.',
  'X-ray documents preserved tibiofemoral joint space without advanced bone-on-bone osteoarthritis, supporting meniscal mechanical symptoms rather than degenerative end-stage OA as the primary pain generator.',
  'Conservative care failed after 8 weeks of supervised therapy from 03/18/2026 through 05/13/2026, home exercise, activity modification, anti-inflammatory medication when tolerated, and intra-articular corticosteroid injection on 04/23/2026 without sustained relief.',
  'Medical clearance documents no acute contraindication to right knee arthroscopy and confirms stable outpatient hospital anesthesia risk.',
  'No separate authorization is requested for chondroplasty; the requested authorization is limited to CPT 29880.',
];
const nolanPostOpFacts = [
  'Operative report 06/11/2026 documents right knee arthroscopy with partial medial and lateral meniscectomy, CPT 29880. No separate chondroplasty CPT is requested. No complications.',
];
const nolanClinicalSummary = [...nolanPreAuthFacts, ...nolanPostOpFacts].join(' ');
const nolanDocs: OrthopedicPacketDoc[] = [
  makeSyntheticDoc({
    patientKey: 'nolan',
    type: 'MRI Right Knee - Medial and Lateral Meniscus Tears',
    uploadedAt: '2026-05-16T13:00:00.000Z',
    extractedText:
      'MRI right knee 05/16/2026: displaced posterior horn medial meniscus tear, complex lateral meniscus tear, small effusion, and grade 2 to 3 patellofemoral chondromalacia. No fracture.',
  }),
  makeSyntheticDoc({
    patientKey: 'nolan',
    type: 'Orthopedic Evaluation - Mechanical Right Knee Symptoms',
    uploadedAt: '2026-05-20T10:30:00.000Z',
    extractedText:
      'Orthopedic evaluation 05/20/2026: recurrent locking, catching, swelling, positive McMurray, medial and lateral joint-line tenderness, and mechanical symptoms despite conservative care. Right knee arthroscopy with partial medial and lateral meniscectomy recommended.',
  }),
  makeSyntheticDoc({
    patientKey: 'nolan',
    type: 'X-Ray Right Knee - Preserved Joint Space',
    uploadedAt: '2026-05-20T10:00:00.000Z',
    extractedText:
      'X-ray right knee 05/20/2026: mild to moderate degenerative changes with preserved tibiofemoral joint space and no bone-on-bone osteoarthritis. Findings support meniscal mechanical symptoms as the surgical indication.',
  }),
  makeSyntheticDoc({
    patientKey: 'nolan',
    type: 'Conservative Care Summary - Right Knee Meniscus Tear',
    uploadedAt: '2026-05-20T09:45:00.000Z',
    extractedText:
      'Conservative care summary: patient completed 8 weeks of supervised therapy from 03/18/2026 through 05/13/2026 with continued home exercise program. Intra-articular corticosteroid injection on 04/23/2026 provided no sustained relief. Mechanical locking and catching persisted despite therapy, home exercise, activity modification, anti-inflammatory medication when tolerated, and injection.',
  }),
  makeSyntheticDoc({
    patientKey: 'nolan',
    type: 'Medical Clearance - Right Knee Arthroscopy',
    uploadedAt: '2026-05-28T12:00:00.000Z',
    extractedText:
      'Medical clearance 05/28/2026: no acute medical contraindication to right knee arthroscopy in the hospital outpatient setting. Cardiopulmonary status stable and anesthesia risk reviewed. No separate chondroplasty authorization is requested.',
  }),
  makeSyntheticDoc({
    patientKey: 'nolan',
    type: 'Operative Report - Right Knee Arthroscopy Meniscectomy',
    uploadedAt: '2026-06-11T15:00:00.000Z',
    extractedText: nolanPostOpFacts.join(' '),
  }),
];
const nolanTemplate: SurgeonTemplate = {
  id: 'demo-ortho-template-nolan-right-knee-arthroscopy',
  surgeon_id: DEMO_SURGEON_ID,
  template_name: 'Right knee arthroscopy with partial medial and lateral meniscectomy',
  specialty: 'orthopedics',
  body_region: 'knee',
  laterality_required: true,
  primary_cpts: [
    { code: '29880', description: 'Right knee arthroscopy with partial medial and lateral meniscectomy' },
  ],
  secondary_cpts: [],
  narrative_template: null,
  common_addons: [],
  diagnosis_codes: [
    { code: 'S83.241A', description: 'Other tear of medial meniscus, right knee, initial encounter' },
    { code: 'S83.281A', description: 'Other tear of lateral meniscus, right knee, initial encounter' },
    { code: 'M22.41', description: 'Chondromalacia patellae, right knee' },
  ],
  metadata: {
    source: 'LINK synthetic orthopedic packet',
    packet_patient: 'Helen Nolan',
    operating_surgeon_name: 'Dr. John J. Larkin, MD',
    bundled_chondroplasty: true,
    default_high_complexity: false,
    assistant_note_enabled_default: false,
  },
  version: 1,
  is_active: true,
  is_demo: true,
  created_at: DEMO_NOW,
  updated_at: DEMO_NOW,
};
const nolanCase = buildSyntheticCase({
  id: 'demo-ortho-scheduled-nolan-right-knee-arthroscopy',
  patientName: 'Helen Nolan',
  patientDob: '1949-11-03',
  patientMrn: 'DEMO-SCOPE-1001',
  patientSex: 'female',
  scheduledDate: '2026-06-11T11:00:00-04:00',
  template: nolanTemplate,
  encounterId: 'DEMO-SCOPE-9001',
  payerName: 'Medicare Advantage',
  docs: nolanDocs,
});

export const ORTHOPEDIC_PACKET_DEMO_PROFILES: OrthopedicPacketProfile[] = [
  {
    scheduledCase: ackmanCase,
    template: ackmanTemplate,
    docs: ackmanDocs,
    preAuthDocs: getRawPreAuthDocs(ackmanDocs),
    postOpDocs: getRawPostOpDocs(ackmanDocs),
    clinicalSummary: ackmanClinicalSummary,
    laterality: 'right',
    clinicalMetrics: {
      indication: 'post-TKA arthrofibrosis',
      formal_supervised_pt: true,
      pre_manipulation_rom: '0 to 100 degrees',
      post_manipulation_rom: '0 to 130 degrees',
      medical_clearance_reviewed: true,
      hba1c: 7.2,
      bmi: 36.4,
      hospital_site_of_care_supported: true,
    },
  },
  {
    scheduledCase: brownCase,
    template: brownTemplate,
    docs: brownDocs,
    preAuthDocs: getRawPreAuthDocs(brownDocs),
    postOpDocs: getRawPostOpDocs(brownDocs),
    clinicalSummary: brownClinicalSummary,
    laterality: 'left',
    clinicalMetrics: {
      indication: 'acute traumatic extensor mechanism disruption',
      urgent_bypass: true,
      imaging_confirmation: 'MRI confirms distal quadriceps tendon fibers and VMO insertional disruption',
      functional_deficit: 'extensor lag, weakness, and difficulty performing straight leg raise',
    },
  },
  {
    scheduledCase: sarahJenkinsCase,
    template: sarahJenkinsTemplate,
    docs: sarahJenkinsDocs,
    preAuthDocs: getRawPreAuthDocs(sarahJenkinsDocs),
    postOpDocs: getRawPostOpDocs(sarahJenkinsDocs),
    clinicalSummary: sarahJenkinsClinicalSummary,
    laterality: 'right',
    clinicalMetrics: {
      indication: 'right shoulder full-thickness rotator cuff tear with outlet impingement and biceps instability',
      imaging_confirmation: 'MRI confirms full-thickness full-width supraspinatus tear with 2 cm medial retraction, high-grade partial infraspinatus tear, biceps subluxation/tendinosis, and severe bursitis',
      conservative_care_weeks: 8,
      supervised_pt_sessions: 16,
      meloxicam_trial_weeks: 6,
      injection_relief_percent: 15,
      injection_relief_days: 4,
      ncci_distinct_structures: ['rotator cuff footprint', 'subacromial outlet', 'long-head biceps'],
      add_on_cpts: ['29826'],
    },
  },
  {
    scheduledCase: palmerCase,
    template: palmerTemplate,
    docs: palmerDocs,
    preAuthDocs: getRawPreAuthDocs(palmerDocs),
    postOpDocs: getRawPostOpDocs(palmerDocs),
    clinicalSummary: palmerClinicalSummary,
    laterality: 'left',
    clinicalMetrics: {
      indication: 'left chronic rotator cuff tear with symptomatic AC arthrosis',
      imaging_confirmation: 'MRI confirms full-thickness supraspinatus tear and severe AC arthrosis',
      conservative_care_weeks: 12,
      ncci_distinct_structures: ['rotator cuff outlet', 'acromioclavicular joint'],
      bmi: 29.8,
    },
  },
  {
    scheduledCase: vegaCase,
    template: vegaTemplate,
    docs: vegaDocs,
    preAuthDocs: getRawPreAuthDocs(vegaDocs),
    postOpDocs: getRawPostOpDocs(vegaDocs),
    clinicalSummary: vegaClinicalSummary,
    laterality: 'left',
    clinicalMetrics: {
      indication: 'severe left carpal tunnel syndrome',
      emg_confirmation: 'severe left median neuropathy at the wrist',
      failed_conservative_care: true,
      bmi: 27.6,
    },
  },
  {
    scheduledCase: wilderCase,
    template: wilderTemplate,
    docs: wilderDocs,
    preAuthDocs: getRawPreAuthDocs(wilderDocs),
    postOpDocs: getRawPostOpDocs(wilderDocs),
    clinicalSummary: wilderClinicalSummary,
    laterality: 'left',
    clinicalMetrics: {
      indication: 'end-stage left knee tricompartmental osteoarthritis',
      radiographic_grade: 'Kellgren-Lawrence grade 4 bone-on-bone',
      conservative_care_weeks: 24,
      bmi: 44,
      hba1c: 6.4,
      cotinine_ng_ml: 4,
      high_bmi_risk_mitigated: true,
    },
  },
  {
    scheduledCase: nolanCase,
    template: nolanTemplate,
    docs: nolanDocs,
    preAuthDocs: getRawPreAuthDocs(nolanDocs),
    postOpDocs: getRawPostOpDocs(nolanDocs),
    clinicalSummary: nolanClinicalSummary,
    laterality: 'right',
    clinicalMetrics: {
      indication: 'right medial and lateral meniscus tears with mechanical symptoms',
      imaging_confirmation: 'MRI confirms displaced medial and complex lateral meniscus tears',
      mechanical_symptoms: ['locking', 'catching', 'effusion'],
      chondroplasty_bundled: true,
    },
  },
];

export const ORTHOPEDIC_PACKET_DEMO_CASES: ScheduledCase[] =
  ORTHOPEDIC_PACKET_DEMO_PROFILES.map((profile) => profile.scheduledCase);

export const ORTHOPEDIC_PACKET_DEMO_TEMPLATES: SurgeonTemplate[] =
  ORTHOPEDIC_PACKET_DEMO_PROFILES.map((profile) => profile.template);

export const ORTHOPEDIC_PACKET_DEMO_BY_SCHEDULED_CASE: Record<string, OrthopedicPacketProfile> =
  Object.fromEntries(
    ORTHOPEDIC_PACKET_DEMO_PROFILES.map((profile) => [profile.scheduledCase.id, profile]),
  );

export function shouldUseOrthopedicPacketDemo(): boolean {
  if (typeof window === 'undefined') return false;

  const params = new URLSearchParams(window.location.search);
  return (
    params.get('demo') === 'true' ||
    params.get('syntheticPackets') === 'true' ||
    window.localStorage.getItem('link_demo_mode') === 'true'
  );
}

export function getOrthopedicDemoTemplates(specialty?: string | null): SurgeonTemplate[] {
  if (specialty && specialty !== 'orthopedics') return [];
  return ORTHOPEDIC_PACKET_DEMO_TEMPLATES;
}

export function getOrthopedicDemoCases(query?: string): ScheduledCase[] {
  const normalizedQuery = query?.trim().toLowerCase();
  if (!normalizedQuery) return ORTHOPEDIC_PACKET_DEMO_CASES;

  return ORTHOPEDIC_PACKET_DEMO_PROFILES.filter((profile) => {
    const sc = profile.scheduledCase;
    return [
      sc.patient_name,
      sc.patient_mrn,
      sc.encounter_id,
      sc.payer_name,
      sc.scheduled_procedure,
      ...profile.template.primary_cpts.map((cpt) => cpt.code),
      ...profile.template.secondary_cpts.map((cpt) => cpt.code),
      ...profile.template.diagnosis_codes.map((diagnosis) => diagnosis.code),
    ]
      .filter(Boolean)
      .some((value) => value!.toLowerCase().includes(normalizedQuery));
  }).map((profile) => profile.scheduledCase);
}

function modifierForLaterality(laterality: Laterality): string {
  if (laterality === 'right') return 'RT';
  if (laterality === 'left') return 'LT';
  return '50';
}

export function getOrthopedicDemoPostOpCases(): OrthopedicPacketPostOpCase[] {
  return ORTHOPEDIC_PACKET_DEMO_PROFILES.map((profile) => ({
    scheduledCase: {
      ...profile.scheduledCase,
      pre_loaded_docs: profile.postOpDocs,
      clinical_metrics: profile.clinicalMetrics,
    },
    template: profile.template,
    laterality: profile.laterality,
    clinicalMetrics: profile.clinicalMetrics,
    cptLines: [
      ...profile.template.primary_cpts.map((cpt): CaseCptLine => ({
        code: cpt.code,
        description: cpt.description,
        modifiers: [modifierForLaterality(profile.laterality)],
        section: 'primary',
        fromTemplate: true,
      })),
      ...profile.template.secondary_cpts.map((cpt): CaseCptLine => ({
        code: cpt.code,
        description: cpt.description,
        modifiers: getSecondaryDefaultModifiers(cpt.default_modifiers),
        section: 'secondary',
        fromTemplate: true,
      })),
    ],
  }));
}

export function mergeOrthopedicDemoCases(cases: ScheduledCase[], query?: string): ScheduledCase[] {
  const seen = new Set(cases.map((c) => c.id));
  const demoCases = getOrthopedicDemoCases(query).filter((c) => !seen.has(c.id));
  return [...demoCases, ...cases];
}

export function mergeOrthopedicDemoTemplates(
  templates: SurgeonTemplate[],
  specialty?: string | null,
): SurgeonTemplate[] {
  const seen = new Set(templates.map((t) => t.id));
  const demoTemplates = getOrthopedicDemoTemplates(specialty).filter((t) => !seen.has(t.id));
  return [...demoTemplates, ...templates];
}

export function getPacketClinicalText(scheduledCase: ScheduledCase): string {
  const docs = scheduledCase.pre_loaded_docs || [];
  return docs
    .map((doc) => ('extracted_text' in doc ? doc.extracted_text : ''))
    .filter(Boolean)
    .join('\n\n');
}
